import { useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { api, API, errText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, ScrollText, ExternalLink, Send, CheckCircle2, XCircle, Ban } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fdt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

const STATUS = {
  sent: { label: "Sent", cls: "border-green-200 bg-green-50 text-green-700", icon: CheckCircle2 },
  send_failed: { label: "Send failed", cls: "border-red-200 bg-red-50 text-red-700", icon: XCircle },
  blocked: { label: "Blocked", cls: "border-amber-200 bg-amber-50 text-amber-700", icon: Ban },
};

export default function AuditPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendRec, setResendRec] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/audit").then((r) => setRecords(r.data.records || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const doResend = async () => {
    setBusy(true);
    try {
      await api.post(`/audit/${resendRec.id}/resend`, { recipient_email: email });
      toast.success(`Resent to ${email}`);
      setResendRec(null);
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Resend failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="mx-auto max-w-[1400px] px-6 py-6">
        <div className="mb-5 flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-slate-400" />
          <h1 className="font-head text-2xl font-600 tracking-tight text-slate-900">Audit history</h1>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : records.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No audit records yet. Sent and blocked quotes appear here.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Ref</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Validation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>By (app / Lightspeed)</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => {
                  const st = STATUS[r.status] || { label: r.status, cls: "border-slate-200 text-slate-600", icon: CheckCircle2 };
                  const StIcon = st.icon;
                  return (
                    <TableRow key={r.id} data-testid={`audit-row-${r.reference}`}>
                      <TableCell className="font-mono font-500">#{r.reference}</TableCell>
                      <TableCell className="text-slate-600">{r.outlet_name}</TableCell>
                      <TableCell className="text-slate-600">{r.recipient_email}</TableCell>
                      <TableCell className="text-right font-mono">{money(r.total)}</TableCell>
                      <TableCell>
                        {r.validation_passed ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="h-3.5 w-3.5" />Exact</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-700"><XCircle className="h-3.5 w-3.5" />Mismatch</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`gap-1 ${st.cls}`}>
                          <StIcon className="h-3 w-3" />{st.label}
                          {r.resend_count > 0 ? ` ·${r.resend_count}` : ""}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        <div className="text-slate-700">{r.app_user_name || r.app_user_email}</div>
                        <div>{r.ls_user_identity || "—"}</div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{fdt(r.sent_at || r.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {r.download_token && (
                            <a
                              href={`${API}/public/quote-pdf/${r.download_token}`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`audit-pdf-${r.reference}`}
                              className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                              title="Open PDF"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          {r.download_token && (
                            <button
                              onClick={() => { setResendRec(r); setEmail(r.recipient_email || ""); }}
                              data-testid={`audit-resend-${r.reference}`}
                              className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600"
                              title="Resend"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      <Dialog open={!!resendRec} onOpenChange={(o) => !o && setResendRec(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-head">Resend quote #{resendRec?.reference}</DialogTitle>
          </DialogHeader>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="resend-email" className="focus-visible:ring-2 focus-visible:ring-blue-500" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendRec(null)}>Cancel</Button>
            <Button onClick={doResend} disabled={busy || !email} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Resend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
